'use client'

import React, { useState, useEffect, useMemo, useRef } from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import { usePathname } from 'next/navigation'
import { getBranchCodeFromPath, regionalizePath } from '@/lib/config/branches-config'
import { useLanguage } from '@/lib/config/useLanguage'

export interface CategoryItem {
  id: string
  name: string
  href: string
  children?: CategoryItem[]
  primary_menu?: boolean
}

// Структура каталога металлоконструкций (fallback)
const mockCategories: CategoryItem[] = [
  {
    id: 'metallokonstruktsii',
    name: 'Металлоконструкции',
    href: '/shop/category/metallokonstruktsii',
    children: [
      {
        id: 'stroitelnye-metallokonstruktsii',
        name: 'Строительные металлоконструкции',
        href: '/shop/category/stroitelnye-metallokonstruktsii',
      },
      {
        id: 'metallicheskie-fermy',
        name: 'Металлические фермы',
        href: '/shop/category/metallicheskie-fermy',
        children: [
          { id: 'arochnye-fermy', name: 'Арочные фермы', href: '/shop/category/arochnye-fermy' },
          { id: 'balochnye-fermy', name: 'Балочные фермы', href: '/shop/category/balochnye-fermy' },
          { id: 'parallelnye-fermy', name: 'Параллельные фермы', href: '/shop/category/parallelnye-fermy' },
          { id: 'poligonalnye-fermy', name: 'Полигональные фермы', href: '/shop/category/poligonalnye-fermy' },
          { id: 'portalnye-fermy', name: 'Портальные фермы', href: '/shop/category/portalnye-fermy' },
          { id: 'segmentnye-fermy', name: 'Сегментные фермы', href: '/shop/category/segmentnye-fermy' },
          { id: 'trapetseidalnye-fermy', name: 'Трапецеидальные фермы', href: '/shop/category/trapetseidalnye-fermy' },
          { id: 'fermy-kombinirovannye', name: 'Фермы комбинированные', href: '/shop/category/fermy-kombinirovannye' },
          { id: 'fermy-ramnye', name: 'Фермы рамные', href: '/shop/category/fermy-ramnye' },
          { id: 'fermy-shedovye', name: 'Фермы шедовые', href: '/shop/category/fermy-shedovye' },
          { id: 'treugolnye-fermy', name: 'Треугольные фермы', href: '/shop/category/treugolnye-fermy' },
          { id: 'odnoskatnye-fermy', name: 'Односкатные фермы', href: '/shop/category/odnoskatnye-fermy' },
          { id: 'dvuhskatnye-fermy', name: 'Двухскатные фермы', href: '/shop/category/dvuhskatnye-fermy' },
          { id: 'fermy-iz-profilnoj-truby', name: 'Фермы из профильной трубы', href: '/shop/category/fermy-iz-profilnoj-truby' },
          { id: 'fermy-iz-ugolka', name: 'Фермы из уголка', href: '/shop/category/fermy-iz-ugolka' },
        ]
      },
      {
        id: 'metallicheskie-ploshchadki-obsluzhivaniya',
        name: 'Металлические площадки',
        href: '/shop/category/metallicheskie-ploshchadki-obsluzhivaniya',
        children: [
          { id: 'vspomogatelnye-ploshchadki', name: 'Вспомогательные площадки', href: '/shop/category/vspomogatelnye-ploshchadki' },
          { id: 'ploshchadki-tehnologicheskie', name: 'Площадки технологические', href: '/shop/category/ploshchadki-tehnologicheskie' },
          { id: 'ploshchadki-dlya-oborudovaniya', name: 'Площадки для оборудования', href: '/shop/category/ploshchadki-dlya-oborudovaniya' },
          { id: 'ploshchadki-dlya-proizvodstva', name: 'Площадки для производства', href: '/shop/category/ploshchadki-dlya-proizvodstva' },
          { id: 'ploshchadki-smotrovye', name: 'Площадки смотровые', href: '/shop/category/ploshchadki-smotrovye' },
          { id: 'ploshchadki-obsluzhivaniya', name: 'Площадки обслуживания', href: '/shop/category/ploshchadki-obsluzhivaniya' },
          { id: 'ploshchadki-kak-dopolnitelnyj-etazh-dlya-ofisa', name: 'Площадки,как дополнителый этаж для офиса', href: '/shop/category/ploshchadki-kak-dopolnitelnyj-etazh-dlya-ofisa' },
          { id: 'ploshchadki-kak-dopolnitelnyj-etazh-dlya-sklada', name: 'Площадки,как дополнителый этаж для склада', href: '/shop/category/ploshchadki-kak-dopolnitelnyj-etazh-dlya-sklada' },
        ]
      },
      {
        id: 'armaturnye-karkasy',
        name: 'Арматурные каркасы',
        href: '/shop/category/armaturnye-karkasy',
      },
      {
        id: 'gnutye-elementy-iz-armatury',
        name: 'Гнутые элементы из арматуры',
        href: '/shop/category/gnutye-elementy-iz-armatury',
      },
      {
        id: 'svarnye-dvutavrovye-balki',
        name: 'Сварные двутавровые балки',
        href: '/shop/category/svarnye-dvutavrovye-balki',
      },
      {
        id: 'metallicheskie-kolonny',
        name: 'Металлические колонны',
        href: '/shop/category/metallicheskie-kolonny',
      },
      {
        id: 'metallicheskie-stojki',
        name: 'Металлические стойки',
        href: '/shop/category/metallicheskie-stojki',
      },
      {
        id: 'rigeli',
        name: 'Ригели',
        href: '/shop/category/rigeli',
      },
      {
        id: 'zakladnye-detali',
        name: 'Закладные детали',
        href: '/shop/category/zakladnye-detali',
      },
      {
        id: 'podkranovye-konstruktsii',
        name: 'Подкрановые конструкции',
        href: '/shop/category/podkranovye-konstruktsii',
      },
      {
        id: 'progony',
        name: 'Прогоны',
        href: '/shop/category/progony',
      },
      {
        id: 'fahverki-metallicheskie',
        name: 'Фахверки металлические',
        href: '/shop/category/fahverki-metallicheskie',
      },
      {
        id: 'metallicheskie-rostverki',
        name: 'Металлические ростверки',
        href: '/shop/category/metallicheskie-rostverki',
      },
      {
        id: 'metallicheskie-ograzhdeniya',
        name: 'Металлические ограждения',
        href: '/shop/category/metallicheskie-ograzhdeniya',
      },
      {
        id: 'ankernye-sistemy',
        name: 'Анкерные системы',
        href: '/shop/category/ankernye-sistemy',
      },
      {
        id: 'proizvodstvo-dymohodov-i-gazohodov',
        name: 'Производство дымоходов и газоходов',
        href: '/shop/category/proizvodstvo-dymohodov-i-gazohodov',
      },
      {
        id: 'emkostnoe-oborudovanie',
        name: 'Емкостное оборудование',
        href: '/shop/category/emkostnoe-oborudovanie',
      },
      {
        id: 'metallokassety',
        name: 'Металлокассеты',
        href: '/shop/category/metallokassety',
      },
      {
        id: 'reshetchatyj-nastil',
        name: 'Решетчатый настил',
        href: '/shop/category/reshetchatyj-nastil',
      },
      {
        id: 'vintovye-svai',
        name: 'Винтовые сваи',
        href: '/shop/category/vintovye-svai',
      },
      {
        id: 'lestnitsy-promyshlennye',
        name: 'Лестницы промышленные',
        href: '/shop/category/lestnitsy-promyshlennye',
      },
      {
        id: 'vintovye-podemniki',
        name: 'Винтовые подъемники',
        href: '/shop/category/vintovye-podemniki',
      },
      {
        id: 'kurilki',
        name: 'Курилки',
        href: '/shop/category/kurilki',
      },
      {
        id: 'besedki',
        name: 'Беседки',
        href: '/shop/category/besedki',
      },
      {
        id: 'nesushchie-metallicheskie-konstruktsii',
        name: 'Несущие металлические конструкции',
        href: '/shop/category/nesushchie-metallicheskie-konstruktsii',
      },
      {
        id: 'izgotovlenie-metallicheskih-estakad',
        name: 'Изготовление металлических эстакад',
        href: '/shop/category/izgotovlenie-metallicheskih-estakad',
      },
      {
        id: 'tehnologicheskie-metallokonstruktsii',
        name: 'Технологические металлоконструкции',
        href: '/shop/category/tehnologicheskie-metallokonstruktsii',
      },
      {
        id: 'izgotovlenie-sborno-razbornyh-metallokonstruktsij',
        name: 'Изготовление сборно-разборных металлоконструкций',
        href: '/shop/category/izgotovlenie-sborno-razbornyh-metallokonstruktsij',
      },
      {
        id: 'svarnye-metallokonstruktsii',
        name: 'Сварные металлоконструкции',
        href: '/shop/category/svarnye-metallokonstruktsii',
      },
      {
        id: 'ramnye-metallokonstruktsii',
        name: 'Рамные металлоконструкции',
        href: '/shop/category/ramnye-metallokonstruktsii',
      },
      {
        id: 'kronshtejny',
        name: 'Кронштейны',
        href: '/shop/category/kronshtejny',
      },
      {
        id: 'korpusa-oborudovaniya',
        name: 'Корпусы оборудования',
        href: '/shop/category/korpusa-oborudovaniya',
      },
      {
        id: 'ramy-pod-oborudovanie',
        name: 'Рамы под оборудование',
        href: '/shop/category/ramy-pod-oborudovanie',
      },
      {
        id: 'reshetki',
        name: 'Решетки',
        href: '/shop/category/reshetki',
      },
      {
        id: 'navesy',
        name: 'Навесы',
        href: '/shop/category/navesy',
      },
      {
        id: 'kolesootbojniki',
        name: 'Колесоотбойники',
        href: '/shop/category/kolesootbojniki',
      },
      {
        id: 'zonty-na-vyhody-ventilyatsii',
        name: 'Зонты на выходы вентиляции',
        href: '/shop/category/zonty-na-vyhody-ventilyatsii',
      },
      {
        id: 'tipovye-resheniya',
        name: 'Типовые решения',
        href: '/shop/category/tipovye-resheniya',
      },
      {
        id: 'opornye-metallokonstruktsii',
        name: 'Опорные металлоконструкции',
        href: '/shop/category/opornye-metallokonstruktsii',
      },
    ]
  },
  {
    id: 'bystrovozvodimye-zdaniya',
    name: 'Быстровозводимые здания',
    href: '/shop/category/bystrovozvodimye-zdaniya',
    children: [
      {
        id: 'bystrovozvodimye-zdaniya-iz-metallokonstruktsij',
        name: 'Быстровозводимые здания из металлоконструкций',
        href: '/shop/category/bystrovozvodimye-zdaniya-iz-metallokonstruktsij',
      },
      {
        id: 'modulnye-zdaniya',
        name: 'Модульные здания',
        href: '/shop/category/modulnye-zdaniya',
      },
      {
        id: 'protivodronnaya-zashchita-ot-bpla',
        name: 'Противодронная защита от БПЛА',
        href: '/shop/category/protivodronnaya-zashchita-ot-bpla',
      },
    ]
  },
  {
    id: 'metalloprokat',
    name: 'Металлопрокат',
    href: '/shop/category/metalloprokat',
    children: [
      {
        id: 'chernyj-metalloprokat',
        name: 'Черный металлопрокат',
        href: '/shop/category/chernyj-metalloprokat',
      },
      {
        id: 'nerzhavejushchij-metalloprokat',
        name: 'Нержавеющий металлопрокат',
        href: '/shop/category/nerzhavejushchij-metalloprokat',
      },
      {
        id: 'alyuminievyj-metalloprokat',
        name: 'Алюминиевый металлопрокат',
        href: '/shop/category/alyuminievyj-metalloprokat',
      },
      {
        id: 'latunnyj-metalloprokat',
        name: 'Латунный металлопрокат',
        href: '/shop/category/latunnyj-metalloprokat',
      },
      {
        id: 'mednyj-prokat',
        name: 'Медный прокат',
        href: '/shop/category/mednyj-prokat',
      },
      {
        id: 'dyuralevyj-prokat',
        name: 'Дюралевый прокат',
        href: '/shop/category/dyuralevyj-prokat',
      },
    ]
  },
]

interface CatalogModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  categories?: CategoryItem[]
}

const ITEMS_LIMIT = 5

export default function CatalogModal({ open, onOpenChange, categories = [] }: CatalogModalProps) {
  const [hoveredCategory, setHoveredCategory] = useState<string | null>(null)
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set())
  
  // Mobile navigation state
  const [mobileNavStack, setMobileNavStack] = useState<CategoryItem[]>([])
  const [currentMobileCategory, setCurrentMobileCategory] = useState<CategoryItem | null>(null)
  
  // Ref для отслеживания, был ли установлен первый элемент при текущем открытии
  const hasSetInitialCategory = useRef(false)
  
  const loading = false // Данные уже загружены на сервере
  const error = null // Ошибки обрабатываются на сервере
  
  // Получаем текущий регион и язык
  const pathname = usePathname() || '/'
  const currentBranchCode = getBranchCodeFromPath(pathname)
  const { localizePath } = useLanguage()
  
  // Функция для комбинирования языковой и региональной локализации
  const localizeAndRegionalizePath = (path: string): string => {
    const localizedPath = localizePath(path)
    return regionalizePath(localizedPath, currentBranchCode)
  }
  
  // Обрабатываем категории с учетом региона
  const processedCategories = useMemo(() => {
    const processCategory = (category: CategoryItem): CategoryItem => {
      const localizedPath = category.href ? localizePath(category.href) : ''
      const regionalizedPath = localizedPath ? regionalizePath(localizedPath, currentBranchCode) : ''
      return {
        ...category,
        href: regionalizedPath,
        children: category.children?.map(processCategory)
      }
    }
    // Используем categories из props или fallback на mockCategories
    const categoriesToProcess = categories.length > 0 ? categories : mockCategories
    return categoriesToProcess.map(processCategory)
  }, [currentBranchCode, localizePath, categories])
  
  // Фильтруем категории для отображения слева - только с primary_menu: true
  const primaryCategories = useMemo(() => {
    return processedCategories.filter(category => category.primary_menu === true)
  }, [processedCategories])
  
  // Все категории для поиска при наведении (включая те, что не primary_menu)
  const allCategories = useMemo(() => {
    return processedCategories
  }, [processedCategories])

  const handleExpand = (itemId: string, event: React.MouseEvent) => {
    event.preventDefault()
    event.stopPropagation()
    setExpandedItems(prev => {
      const newSet = new Set(prev)
      if (newSet.has(itemId)) {
        newSet.delete(itemId)
      } else {
        newSet.add(itemId)
      }
      return newSet
    })
  }

  const handleMobileCategoryClick = (category: CategoryItem) => {
    if (category.children && category.children.length > 0) {
      setMobileNavStack(prev => [...prev, category])
      setCurrentMobileCategory(category)
    }
  }

  const handleMobileBack = () => {
    if (mobileNavStack.length > 0) {
      const newStack = mobileNavStack.slice(0, -1)
      setMobileNavStack(newStack)
      setCurrentMobileCategory(newStack.length > 0 ? newStack[newStack.length - 1] : null)
    } else {
      setCurrentMobileCategory(null)
    }
  }

  const handleMobileRoot = () => {
    setMobileNavStack([])
    setCurrentMobileCategory(null)
  }

  const renderCategoryItems = (items: CategoryItem[] | undefined, level: number = 0) => {
    if (!items || items.length === 0) return null

    return items.map((item) => {
      const hasChildren = item.children && item.children.length > 0
      const isExpanded = expandedItems.has(item.id)
      const showLimit = level === 2 && !isExpanded
      const displayItems = showLimit ? item.children!.slice(0, ITEMS_LIMIT) : item.children
      const hasMore = showLimit && item.children!.length > ITEMS_LIMIT

      return (
        <div key={item.id} className="mb-4">
          <a
            href={item.href}
            className="block text-sm font-medium text-gray-900 hover:text-blue-600 transition-colors mb-2"
          >
            {item.name}
          </a>
          {hasChildren && (
            <ul className="ml-4 space-y-2">
              {displayItems?.map((child) => (
                <li key={child.id}>
                  <a
                    href={child.href}
                    className="block text-sm text-gray-600 hover:text-blue-600 transition-colors"
                  >
                    {child.name}
                  </a>
                </li>
              ))}
              {hasMore && (
                <li>
                  <button
                    onClick={(e) => handleExpand(item.id, e)}
                    className="text-sm text-blue-600 hover:text-blue-800 font-medium flex items-center gap-1"
                  >
                    Ещё
                    <ChevronRight className={cn("h-4 w-4 transition-transform", isExpanded && "rotate-90")} />
                  </button>
                </li>
              )}
              {isExpanded && item.children!.slice(ITEMS_LIMIT).map((child) => (
                <li key={child.id}>
                  <a
                    href={child.href}
                    className="block text-sm text-gray-600 hover:text-blue-600 transition-colors"
                  >
                    {child.name}
                  </a>
                </li>
              ))}
            </ul>
          )}
        </div>
      )
    })
  }

  const getBreadcrumbPath = () => {
    if (mobileNavStack.length === 0) return 'Каталог'
    return `Каталог / ${mobileNavStack.map(c => c.name).join(' / ')}`
  }

  // Сброс состояния при закрытии и установка первого элемента при открытии
  useEffect(() => {
    if (!open) {
      // Сброс при закрытии
      setHoveredCategory(null)
      setExpandedItems(new Set())
      setMobileNavStack([])
      setCurrentMobileCategory(null)
      hasSetInitialCategory.current = false
    } else if (!hasSetInitialCategory.current) {
      // Автоматическое выделение первого элемента только один раз при открытии
      const firstPrimary = primaryCategories[0]
      if (firstPrimary) {
        setHoveredCategory(firstPrimary.id)
        hasSetInitialCategory.current = true
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl w-[95vw] h-[85vh] lg:h-[80vh] p-0 flex flex-col">
        <DialogHeader className="px-6 pt-6 pb-4 border-b">
          <DialogTitle className="text-2xl font-bold">Каталог</DialogTitle>
        </DialogHeader>
        
        <div className="flex-1 overflow-hidden flex flex-col">
          {/* Состояние загрузки */}
          {loading && (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mb-4"></div>
                <p className="text-gray-600">Загрузка каталога...</p>
              </div>
            </div>
          )}
          
          {/* Состояние ошибки */}
          {error && !loading && (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <p className="text-red-600 mb-2">{error}</p>
                <p className="text-sm text-gray-500">Используются данные по умолчанию</p>
              </div>
            </div>
          )}
          
          {/* Desktop версия */}
          {!loading && (
          <div 
            className="hidden lg:flex flex-1 overflow-hidden"
            onMouseLeave={(e) => {
              // Закрываем меню только если курсор уходит за пределы всего контейнера
              // Проверяем, что курсор действительно ушел за пределы всего блока
              const relatedTarget = e.relatedTarget
              const currentTarget = e.currentTarget
              if (relatedTarget && relatedTarget instanceof Node && currentTarget instanceof Node && !currentTarget.contains(relatedTarget)) {
                // Курсор ушел за пределы контейнера, но не закрываем меню автоматически
                // Меню остается открытым до наведения на другой элемент
              }
            }}
          >
            <div className="w-64 border-r overflow-y-auto bg-gray-50">
              <nav className="p-4 space-y-1">
                {primaryCategories.map((category) => (
                  <a
                    key={category.id}
                    href={category.href}
                    onMouseEnter={() => setHoveredCategory(category.id)}
                    onClick={(e) => {
                      // При клике также устанавливаем активную категорию
                      // Если у категории есть дети, предотвращаем переход по ссылке
                      if (category.children && category.children.length > 0) {
                        e.preventDefault()
                        setHoveredCategory(category.id)
                      }
                    }}
                    className={cn(
                      "flex items-center justify-between px-4 py-3 rounded-lg text-sm font-medium transition-colors cursor-pointer",
                      hoveredCategory === category.id
                        ? "bg-blue-50 text-blue-700"
                        : "text-gray-700 hover:bg-gray-100"
                    )}
                  >
                    <span>{category.name}</span>
                    <ChevronRight className="h-4 w-4 flex-shrink-0" />
                  </a>
                ))}
              </nav>
            </div>
            
            <div 
              className="flex-1 overflow-y-auto p-6"
            >
              {hoveredCategory ? (
                (() => {
                  // Ищем категорию среди всех категорий (не только primary)
                  const category = allCategories.find(c => c.id === hoveredCategory)
                  if (!category || !category.children || category.children.length === 0) {
                    return (
                      <div className="flex items-center justify-center h-full text-gray-400">
                        <p>Нет подкатегорий</p>
                      </div>
                    )
                  }
                  
                  return (
                    <div>
                      {category.href ? (
                        <a 
                          href={category.href}
                          className="block text-lg font-semibold text-gray-900 hover:text-blue-600 transition-colors mb-4"
                        >
                          {category.name}
                        </a>
                      ) : (
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">{category.name}</h3>
                      )}
                      <div className="grid grid-cols-2 gap-6">
                        {category.children.map((secondLevel) => (
                          <div key={secondLevel.id} className="space-y-3">
                            {secondLevel.href ? (
                              <a
                                href={secondLevel.href}
                                className="block text-base font-semibold text-gray-900 hover:text-blue-600 transition-colors"
                              >
                                {secondLevel.name}
                              </a>
                            ) : (
                              <div className="block text-base font-semibold text-gray-900">
                                {secondLevel.name}
                              </div>
                            )}
                            {secondLevel.children && secondLevel.children.length > 0 && (
                              <div className="space-y-2">
                                {(() => {
                                  const isExpanded = expandedItems.has(secondLevel.id)
                                  const showLimit = !isExpanded
                                  const displayItems = showLimit 
                                    ? secondLevel.children.slice(0, ITEMS_LIMIT) 
                                    : secondLevel.children
                                  const hasMore = showLimit && secondLevel.children.length > ITEMS_LIMIT
                                  
                                  return (
                                    <>
                                      {displayItems.map((thirdLevel) => (
                                        <a
                                          key={thirdLevel.id}
                                          href={thirdLevel.href}
                                          className="block text-sm text-gray-600 hover:text-blue-600 transition-colors"
                                        >
                                          {thirdLevel.name}
                                        </a>
                                      ))}
                                      {hasMore && (
                                        <button
                                          onClick={(e) => handleExpand(secondLevel.id, e)}
                                          className="text-sm text-blue-600 hover:text-blue-800 font-medium flex items-center gap-1 mt-2"
                                        >
                                          Ещё
                                          <ChevronRight className={cn("h-4 w-4 transition-transform", isExpanded && "rotate-90")} />
                                        </button>
                                      )}
                                    </>
                                  )
                                })()}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )
                })()
              ) : (
                <div className="flex items-center justify-center h-full text-gray-400">
                  <p>Наведите на категорию для просмотра подкатегорий</p>
                </div>
              )}
            </div>
          </div>
          )}

          {/* Mobile версия */}
          {!loading && (
          <div className="lg:hidden flex flex-col flex-1 overflow-hidden">
            {/* Breadcrumb с кнопкой Назад */}
            <div className="px-4 py-3 border-b bg-gray-50 flex items-center gap-2">
              {currentMobileCategory && (
                <button
                  onClick={handleMobileBack}
                  className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800 font-medium mr-2"
                >
                  <ChevronLeft className="h-4 w-4" />
                  Назад
                </button>
              )}
              <button
                onClick={handleMobileRoot}
                className={cn(
                  "text-sm font-medium",
                  currentMobileCategory ? "text-blue-600 hover:text-blue-800" : "text-gray-900"
                )}
              >
                Каталог
              </button>
              {mobileNavStack.map((category, index) => (
                <React.Fragment key={category.id}>
                  <ChevronRight className="h-4 w-4 text-gray-400" />
                  <button
                    onClick={() => {
                      const newStack = mobileNavStack.slice(0, index + 1)
                      setMobileNavStack(newStack)
                      setCurrentMobileCategory(newStack[newStack.length - 1])
                    }}
                    className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                  >
                    {category.name}
                  </button>
                </React.Fragment>
              ))}
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-4">
              {currentMobileCategory ? (
                <div>
                  {currentMobileCategory.children && currentMobileCategory.children.length > 0 ? (
                    <div className="space-y-2">
                      {currentMobileCategory.children.map((item) => {
                        const hasChildren = item.children && item.children.length > 0
                        const hasHref = item.href && item.href.trim() !== ''
                        
                        return (
                          <div key={item.id}>
                            {hasChildren && !hasHref ? (
                              <button
                                onClick={() => handleMobileCategoryClick(item)}
                                className="w-full flex items-center justify-between px-4 py-3 rounded-lg bg-white border border-gray-200 hover:bg-gray-50 transition-colors"
                              >
                                <span className="text-base font-medium text-gray-900">{item.name}</span>
                                <ChevronRight className="h-5 w-5 text-gray-400" />
                              </button>
                            ) : hasChildren && hasHref ? (
                              <div className="flex gap-2">
                                <a
                                  href={item.href}
                                  onClick={() => onOpenChange(false)}
                                  className="flex-1 px-4 py-3 rounded-lg bg-white border border-gray-200 hover:bg-gray-50 transition-colors"
                                >
                                  <span className="text-base font-medium text-gray-900">{item.name}</span>
                                </a>
                                <button
                                  onClick={() => handleMobileCategoryClick(item)}
                                  className="px-4 py-3 rounded-lg bg-white border border-gray-200 hover:bg-gray-50 transition-colors"
                                >
                                  <ChevronRight className="h-5 w-5 text-gray-400" />
                                </button>
                              </div>
                            ) : hasHref ? (
                              <a
                                href={item.href}
                                onClick={() => onOpenChange(false)}
                                className="block w-full px-4 py-3 rounded-lg bg-white border border-gray-200 hover:bg-gray-50 transition-colors"
                              >
                                <span className="text-base font-medium text-gray-900">{item.name}</span>
                              </a>
                            ) : (
                              <div className="block w-full px-4 py-3 rounded-lg bg-white border border-gray-200 bg-gray-50">
                                <span className="text-base font-medium text-gray-600">{item.name}</span>
                              </div>
                            )}
                          </div>
                        )
                      })}
                    </div>
                  ) : (
                    <p className="text-gray-500 text-center py-8">Нет подкатегорий</p>
                  )}
                </div>
              ) : (
                <div className="space-y-2">
                  {primaryCategories.map((category) => (
                    <button
                      key={category.id}
                      onClick={(e) => {
                        if (category.children && category.children.length > 0) {
                          e.preventDefault()
                          handleMobileCategoryClick(category)
                        } else {
                          onOpenChange(false)
                        }
                      }}
                      className="w-full flex items-center justify-between px-4 py-3 rounded-lg bg-white border border-gray-200 hover:bg-gray-50 transition-colors"
                    >
                      <span className="text-base font-medium text-gray-900">{category.name}</span>
                      {category.children && category.children.length > 0 && (
                        <ChevronRight className="h-5 w-5 text-gray-400" />
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}

